import * as React from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { axiosInstance } from '@/modules/shared/lib/axios'
import { ConfigurationType } from '@/app/constants/configuration/configurationType'
import { toast } from '@/lib/toasts/api'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { formatDate } from '@/modules/shared/utils/date'

export const QUERY_KEY_SETTINGS_PREVIOUS_CONFIGURATION = 'settings.previousConfiguration'

type PreviousConfigurationInfo = {
  available: boolean
  savedAt: string | null
  savedBy: string | null
}

type Options = {
  configurationType: ConfigurationType
  queryKeys: string[]
  // Dane zakładki z serwera: każda ich zmiana (zapis, przywrócenie) oznacza nową wersję wstecz.
  serverData: unknown
}

/** Ostatnia wersja zakładki z historii zapisów (backend, do 10 wersji). */
export function usePreviousConfiguration(
  companyId: string,
  { configurationType, queryKeys, serverData }: Options,
) {
  const queryClient = useQueryClient()
  const { t } = useLocalize()
  const infoKey = [QUERY_KEY_SETTINGS_PREVIOUS_CONFIGURATION, companyId, configurationType]

  const info = useQuery({
    queryKey: infoKey,
    queryFn: async () => {
      const { data } = await axiosInstance.get<PreviousConfigurationInfo>(
        `/api/${companyId}/configuration/previousConfiguration`,
        { params: { configurationType } },
      )
      return data
    },
    enabled: !!companyId,
  })

  React.useEffect(() => {
    if (serverData !== undefined) {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_SETTINGS_PREVIOUS_CONFIGURATION, companyId, configurationType],
      })
    }
  }, [serverData, queryClient, companyId, configurationType])

  const revert = useMutation({
    mutationFn: async () => {
      const { data } = await axiosInstance.post(
        `/api/${companyId}/configuration/revertConfiguration`,
        { configurationType },
      )
      return data
    },
    onError: (error) => {
      console.error('Error reverting configuration:', error)
      toast.error({ text: t('configuration.revert-error').toString(), autoCloseMs: 8000 })
    },
    onSuccess: () => {
      queryKeys.forEach((key) =>
        queryClient.invalidateQueries({ queryKey: [key, companyId] }),
      )
      queryClient.invalidateQueries({ queryKey: infoKey })
      toast.success({
        title: t('configuration.reverted-title').toString(),
        text: t('configuration.reverted-details').toString(),
      })
    },
  })

  const savedAt = info.data?.savedAt
  const confirmMessage = t('configuration.revert-confirm')
    .toString()
    .replace('{0}', savedAt ? formatDate(new Date(savedAt), 'YYYY-MM-DD HH:mm') : '')
    .replace('{1}', info.data?.savedBy || '—')

  return {
    isAvailable: !!info.data?.available,
    revert,
    confirmMessage,
  }
}
